import Humanograma from 'components/Humanograma';

import { BarraDaEtapa, EtapaDaAgenda, Responsavel } from './AgendaDaRealizacao';

/**
 * Cor de quem apreciou: `green-700` dá 5,02:1 no branco do cartão (AA); o `#04D475` do `TaskItem` dava 1,97:1. No
 * cinza (`surface-100`) da agenda de "Mais informações", `green-700` cai a 4,47:1; ali vale `green-800` (6,35:1).
 * O "(apreciou)" para leitor de tela evita que a cor seja a única indicação (WCAG 1.4.1).
 */
const COR_DE_QUEM_APRECIOU = { branco: 'font-bold text-green-700', cinza: 'font-bold text-green-800' };

/** Nome do responsável em azul negrito, como no design; `brand-blue-dark` dá 8,9:1 no branco e 8,0:1 no cinza. */
const COR_DO_RESPONSAVEL = 'font-bold text-brand-blue-dark';

/** Nome de um responsável com a dica do `Humanograma` (RDV-25) e o destaque de quem apreciou (RDV-24). */
export function NomeDoResponsavel({ pessoa, fundo = 'branco' }: { pessoa: Responsavel; fundo?: 'branco' | 'cinza' }) {
  return (
    <>
      <Humanograma
        data={{
          _id: pessoa.chave,
          nome: pessoa.nomeCompleto,
          funcao: pessoa.funcao,
          prefixo: pessoa.prefixo,
          nome_dependencia: pessoa.dependencia,
        }}
      >
        <span className={pessoa.apreciou ? COR_DE_QUEM_APRECIOU[fundo] : COR_DO_RESPONSAVEL}>{pessoa.nome}</span>
      </Humanograma>
      {pessoa.apreciou && <span className="sr-only"> (apreciou)</span>}
    </>
  );
}

export interface TrilhaDaRealizacaoProps {
  etapas: EtapaDaAgenda[];
  /** Texto ao lado da trilha ("Em validação", "1 de 3 níveis"…); a fila não mostra. */
  progresso?: string;
}

/** Colunas no desktop: quatro no participante, cinco na fila com Pit Stop. */
const COLUNAS: Record<number, string> = { 4: 'md:grid-cols-4', 5: 'md:grid-cols-5' };

/** Trilha compacta do cartão (RDV-17): barra, nome, status e responsáveis de cada etapa, sem datas. */
export default function TrilhaDaRealizacao({ etapas, progresso }: TrilhaDaRealizacaoProps) {
  if (!etapas.length) return null;

  return (
    <div className="flex flex-col gap-3 md:flex-row md:items-start md:gap-6">
      <ol
        aria-label="Trilha da apreciação"
        className={`m-0 grid min-w-0 flex-1 list-none grid-cols-2 gap-3 p-0 ${COLUNAS[etapas.length] ?? 'md:grid-cols-4'}`}
      >
        {etapas.map(etapa => (
          <li key={etapa.titulo} data-etapa={etapa.titulo} aria-current={etapa.atual ? 'step' : undefined} className="min-w-0">
            <BarraDaEtapa metades={etapa.metades} />
            <p className="m-0 mt-1.5 text-[11px] font-bold text-brand-navy">{etapa.titulo}</p>
            {etapa.status && <p className="m-0 text-[11px] text-content">{etapa.status}</p>}
            {etapa.responsaveis.length > 0 && (
              <ul className="m-0 mt-1 list-none space-y-0.5 p-0 text-[10px]">
                {etapa.responsaveis.map(pessoa => (
                  <li key={pessoa.chave}>
                    <NomeDoResponsavel pessoa={pessoa} />
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ol>
      {/* SPEC_DEVIATION: texto `brand-blue-dark` no chip `#EDEFFF` (7,6:1); o `brand-blue` do design dá 4,1:1. */}
      {progresso && (
        <p className="m-0 shrink-0 self-start whitespace-nowrap rounded-jj-pill bg-[#EDEFFF] px-[11px] py-[5px] font-display text-[11px] font-bold text-brand-blue-dark">
          {progresso}
        </p>
      )}
    </div>
  );
}
